import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import Button from "components/Button";
import { ROUTES } from "shared/constants/routes";
import COLORS from "shared/constants/colors";
import {
  MIN_ORDER_SUBTOTAL,
  getRushSameDayFee,
  meetsMinOrderSubtotal,
  readRushSameDayPreference,
  writeRushSameDayPreference,
} from "pages/dtf-transfers/by-size/data";
import ContinueShoppingButton from "pages/dtf-transfers/checkout/ContinueShoppingButton";
import {
  CartSidebar,
  CheckoutCard,
  TotalRow,
  TotalLabel,
  TotalAmount,
  SidebarItemList,
  SidebarItem,
  SidebarItemInfo,
  SidebarItemName,
  SidebarItemMeta,
  SidebarItemPrice,
  CheckoutNote,
  MinOrderAlert,
} from "styles/pages/dtf-transfers/cart";
import {
  RushSameDayRow,
  RushSameDayLabel,
} from "styles/pages/dtf-transfers/checkout";

const CartSidebarComponent = ({ subtotal, cartItems = [] }) => {
  const navigate = useNavigate();
  const [rushSameDay, setRushSameDay] = useState(readRushSameDayPreference);
  const rushSameDayFee = getRushSameDayFee(subtotal, rushSameDay);
  const total = Number((subtotal + rushSameDayFee).toFixed(2));
  const meetsMinimum = meetsMinOrderSubtotal(subtotal, rushSameDay);

  const handleRushSameDayChange = (checked) => {
    setRushSameDay(checked);
    writeRushSameDayPreference(checked);
  };

  return (
    <CartSidebar>
      <CheckoutCard>
        <TotalRow>
          <TotalLabel>Total</TotalLabel>
          <TotalAmount>${total.toFixed(2)}</TotalAmount>
        </TotalRow>

        <RushSameDayRow $checked={rushSameDay}>
          <RushSameDayLabel>
            <input
              type="checkbox"
              checked={rushSameDay}
              onChange={(event) =>
                handleRushSameDayChange(event.target.checked)
              }
            />
            <BoltOutlinedIcon />
            Rush Same Day
          </RushSameDayLabel>
        </RushSameDayRow>

        {cartItems.length > 0 ? (
          <SidebarItemList>
            {cartItems.map((item, index) => {
              const primarySize = item.sizes?.[0];
              const quantity = primarySize?.quantity ?? 1;
              const sizeLabel = primarySize
                ? `${primarySize.width}" x ${primarySize.height}"`
                : null;

              return (
                <SidebarItem key={item.id ?? index}>
                  <SidebarItemInfo>
                    <SidebarItemName>
                      {item.fileName || "Cart item"}
                    </SidebarItemName>
                    <SidebarItemMeta>
                      Qty {quantity}
                      {sizeLabel ? ` · ${sizeLabel}` : ""}
                    </SidebarItemMeta>
                  </SidebarItemInfo>
                  <SidebarItemPrice>
                    ${(item.total || 0).toFixed(2)}
                  </SidebarItemPrice>
                </SidebarItem>
              );
            })}
            {rushSameDay ? (
              <SidebarItem>
                <SidebarItemInfo>
                  <SidebarItemName>Rush Same Day</SidebarItemName>
                </SidebarItemInfo>
                <SidebarItemPrice>
                  ${rushSameDayFee.toFixed(2)}
                </SidebarItemPrice>
              </SidebarItem>
            ) : null}
          </SidebarItemList>
        ) : null}

        {!meetsMinimum ? (
          <MinOrderAlert>
            A minimum subtotal of CAD ${MIN_ORDER_SUBTOTAL.toFixed(2)} is
            required to place an order. Please add more items to continue.
          </MinOrderAlert>
        ) : null}

        <Button
          title="Checkout"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(ROUTES.DTF_TRANSFERS_CHECKOUT.LINK)}
          containerStyle={{
            backgroundColor: COLORS.BLUE,
            color: COLORS.WHITE,
            textTransform: "none",
            borderRadius: "6px",
            padding: "12px 20px",
            fontSize: "16px",
            width: "100%",
          }}
        />

        <ContinueShoppingButton fullWidth showDivider={false} />

        <CheckoutNote>Taxes and shipping calculated at checkout</CheckoutNote>
      </CheckoutCard>
    </CartSidebar>
  );
};

export default CartSidebarComponent;
